import { advancedSearchApi, handleError } from '@ardoq/api';
import { QueryBuilderQuery } from '@ardoq/api-types';
import { isArdoqError, isEqual } from '@ardoq/common-helpers';
import { action$, extractPayload, ofType } from '@ardoq/rxbeach';
import {
  enhancedScopeDataOperations,
  scopeDataOperations,
} from '@ardoq/scope-data';

import {
  distinctUntilChanged,
  filter,
  from,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { toScopeDataWithSortedComponentsAndWorkspacesByNames } from './toScopeDataWithSortedComponentsAndWorkspacesByNames';
import { getCurrentLocale } from '@ardoq/locale';
import { EnhancedScopeData } from '@ardoq/data-model';
import { setParentSelectQuery } from '../actions';
import { LoadErrorMessageKey, LoadingMessageKey } from './loadStatusKey';

type WorkspaceQuery = {
  workspaceQuery: QueryBuilderQuery | null;
  componentIds: string[];
};

const workspaceQuery$: Observable<WorkspaceQuery> = action$.pipe(
  ofType(setParentSelectQuery),
  extractPayload(),
  distinctUntilChanged(isEqual),
  startWith({ workspaceQuery: null, componentIds: [] }),
  shareReplay({ bufferSize: 1, refCount: true })
);

const loadScopeData$ = workspaceQuery$.pipe(
  switchMap(({ workspaceQuery, componentIds }) =>
    from(
      workspaceQuery
        ? advancedSearchApi.scopeData({
            query: workspaceQuery,
            minimal: true,
          })
        : of(scopeDataOperations.getEmpty())
    ).pipe(map(response => ({ response, componentIds })))
  ),
  map(({ response, componentIds }) => {
    if (isArdoqError(response)) {
      return response;
    }
    const sortedScopeData = toScopeDataWithSortedComponentsAndWorkspacesByNames(
      response,
      getCurrentLocale()
    );
    const enhancedScopeData = scopeDataOperations.enhance(sortedScopeData);
    if (componentIds.length === 0) {
      return enhancedScopeData;
    }
    const descendants = [
      ...componentIds,
      ...enhancedScopeDataOperations.getDescendantComponentIds(
        enhancedScopeData,
        componentIds
      ),
    ];
    const scopeComponentIds = new Set(
      Object.keys(enhancedScopeData.componentsById)
    ).difference(new Set(descendants));
    return {
      ...enhancedScopeData,
      scopeComponentIds,
    };
  }),
  shareReplay({ bufferSize: 1, refCount: true })
);

export const loadingScopeDataNotification$: Observable<LoadingMessageKey | null> =
  merge(
    workspaceQuery$.pipe(map((): LoadingMessageKey => 'query')),
    loadScopeData$.pipe(map(() => null)),
    from([null])
  );

export const loadScopeDataError$: Observable<LoadErrorMessageKey | null> =
  merge(
    loadScopeData$.pipe(
      filter(data => isArdoqError(data)),
      map((): LoadErrorMessageKey => 'query')
    ),
    workspaceQuery$.pipe(map(() => null)),
    from([null])
  );

export const enhancedScopeData$: Observable<EnhancedScopeData> =
  loadScopeData$.pipe(
    handleError(),
    shareReplay({ bufferSize: 1, refCount: true })
  );
